import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  StickyNote,
  Eye,
  Search,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock3,
  Gauge,
  Sparkles,
} from "lucide-react";

import { issuesData } from "../content";
import { SeverityBadge } from "../components/SeverityBadge";
import { CategoryBadge } from "../components/CategoryBadge";
import { useBookmarks } from "../hooks/useBookmarks";
import { useNotes } from "../hooks/useNotes";

type SectionCardProps = {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionCard({ title, icon, children }: SectionCardProps) {
  return (
    <BluePanel className="mb-6">
      <div className="border-b border-white/8 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-dtf-accent/20 bg-black/25 text-dtf-accent shadow-[0_0_14px_rgba(37,99,235,0.20)]">
            {icon}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-white">{title}</h2>
        </div>
      </div>

      <div className="px-6 py-6">{children}</div>
    </BluePanel>
  );
}

function BulletList({ items }: { items?: string[] }) {
  if (!items?.length) {
    return <p className="text-dtf-text-muted">No details added yet.</p>;
  }

  return (
    <ul className="space-y-5">
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="flex items-start gap-4">
          <span className="mt-2.5 h-3 w-3 flex-shrink-0 rounded-full bg-dtf-accent shadow-[0_0_12px_rgba(245,158,11,0.45)]" />
          <span className="text-dtf-text text-[17px] leading-8">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function NumberedList({ items }: { items?: string[] }) {
  if (!items?.length) {
    return <p className="text-dtf-text-muted">No details added yet.</p>;
  }

  return (
    <ol className="space-y-4">
      {items.map((item, index) => (
        <li
          key={`${item}-${index}`}
          className="flex items-start gap-4 rounded-2xl border border-white/6 bg-black/14 px-4 py-4"
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-dtf-blue/20 text-sm font-bold text-dtf-blue shadow-[0_0_12px_rgba(59,130,246,0.24)]">
            {index + 1}
          </span>
          <span className="pt-0.5 text-dtf-text text-[17px] leading-7">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function MetaPill({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-dtf-text shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      <span className="text-dtf-accent">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

export function IssueDetail() {
  const { id } = useParams<{ id: string }>();
  const { toggleBookmark, isBookmarked } = useBookmarks();
  const { addNote, getNotesByIssueId } = useNotes();

  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const issue = useMemo(() => {
    return issuesData.issues.find((item) => item.id === id);
  }, [id]);

  if (!issue) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <BluePanel>
          <div className="p-8 text-center">
            <h1 className="mb-3 text-2xl font-bold text-white">Issue not found</h1>
            <p className="mb-6 text-dtf-text-muted">
              The issue you tried to open does not exist or may have been removed.
            </p>
            <Link
              to="/issues"
              className="inline-flex items-center gap-2 rounded-xl border border-dtf-accent/25 bg-black/20 px-5 py-3 font-semibold text-white transition hover:border-dtf-accent/40 hover:bg-black/30"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Issues
            </Link>
          </div>
        </BluePanel>
      </div>
    );
  }

  const saved = isBookmarked(issue.id, "issue");
  const notes = getNotesByIssueId(issue.id);

  const handleSaveNote = () => {
    if (!noteTitle.trim() || !noteContent.trim()) return;

    addNote({
      issueId: issue.id,
      title: noteTitle.trim(),
      content: noteContent.trim(),
    });

    setNoteTitle("");
    setNoteContent("");
    setShowNoteForm(false);
  };

  const printerTypeLabel =
    (issue.printerType ?? []).length > 0
      ? issue.printerType
          .map((type: string) => (type === "all" ? "Universal" : type.toUpperCase()))
          .join(", ")
      : null;

  const difficultyClass =
    issue.difficulty === "easy"
      ? "border-green-500/20 bg-green-500/10 text-green-400"
      : issue.difficulty === "intermediate"
      ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-300"
      : issue.difficulty === "advanced"
      ? "border-red-500/20 bg-red-500/10 text-red-400"
      : "border-white/10 bg-black/20 text-dtf-text-muted";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Link
        to="/issues"
        className="mb-6 inline-flex items-center gap-2 text-dtf-text-muted transition hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Issues
      </Link>

      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <CategoryBadge category={issue.category} />
                <SeverityBadge severity={issue.severity} />
                {issue.difficulty && (
                  <span
                    className={`rounded-full border px-3 py-1 text-sm font-medium capitalize ${difficultyClass}`}
                  >
                    {issue.difficulty}
                  </span>
                )}
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                {issue.title}
              </h1>

              <p className="mb-6 max-w-3xl text-lg leading-8 text-dtf-text sm:text-[20px] sm:leading-9">
                {issue.whatItLooksLike}
              </p>

              <div className="flex flex-wrap gap-3">
                {issue.estimatedTime && (
                  <MetaPill
                    icon={<Clock3 className="h-4 w-4" />}
                    label={`Estimated time: ${issue.estimatedTime}`}
                  />
                )}

                {printerTypeLabel && (
                  <MetaPill
                    icon={<Gauge className="h-4 w-4" />}
                    label={`Printer type: ${printerTypeLabel}`}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() =>
                  toggleBookmark({
                    id: issue.id,
                    type: "issue",
                    title: issue.title,
                  })
                }
                className={`inline-flex items-center gap-2 rounded-2xl border px-5 py-3 font-medium transition ${
                  saved
                    ? "border-dtf-blue/30 bg-dtf-blue/10 text-white shadow-[0_0_18px_rgba(59,130,246,0.18)]"
                    : "border-white/10 bg-black/20 text-dtf-text-muted hover:border-dtf-blue/30 hover:bg-black/30 hover:text-white"
                }`}
              >
                <Bookmark className={`h-4 w-4 ${saved ? "fill-current" : ""}`} />
                {saved ? "Saved" : "Save"}
              </button>

              <button
                onClick={() => setShowNoteForm((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 font-medium text-dtf-text-muted transition hover:border-dtf-blue/30 hover:bg-black/30 hover:text-white"
              >
                <StickyNote className="h-4 w-4" />
                Note
              </button>
            </div>
          </div>
        </div>
      </BluePanel>

      {showNoteForm && (
        <BluePanel className="mb-6">
          <div className="border-b border-white/8 px-6 py-4">
            <h3 className="text-xl font-bold text-white">Add Note</h3>
            <p className="mt-1 text-sm text-dtf-text-muted">
              Save what you tested, what failed, and what finally worked.
            </p>
          </div>

          <div className="p-6">
            <input
              type="text"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder="Note title..."
              className="mb-4 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-dtf-gray outline-none transition focus:border-dtf-blue/35"
            />

            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Your observations, settings used, fixes tried, or press/cure changes..."
              rows={5}
              className="mb-4 w-full resize-none rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-dtf-gray outline-none transition focus:border-dtf-blue/35"
            />

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSaveNote}
                className="rounded-2xl bg-dtf-accent px-5 py-3 font-semibold text-dtf-dark transition hover:bg-dtf-accent-hover"
              >
                Save Note
              </button>

              <button
                onClick={() => setShowNoteForm(false)}
                className="rounded-2xl border border-white/10 px-5 py-3 font-medium text-dtf-text-muted transition hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </BluePanel>
      )}

      <SectionCard title="What It Looks Like" icon={<Eye className="h-5 w-5" />}>
        <p className="mb-5 text-dtf-text text-[17px] leading-8">{issue.whatItLooksLike}</p>
        <BulletList items={issue.symptoms} />
      </SectionCard>

      {(issue.quickChecks ?? []).length > 0 && (
        <SectionCard title="Quick Diagnostic Checks" icon={<Search className="h-5 w-5" />}>
          <NumberedList items={issue.quickChecks} />
        </SectionCard>
      )}

      {(issue.causes ?? []).length > 0 && (
        <SectionCard title="Most Likely Causes" icon={<AlertTriangle className="h-5 w-5" />}>
          <NumberedList items={issue.causes} />
        </SectionCard>
      )}

      {(issue.fixes ?? []).length > 0 && (
        <SectionCard title="Step-by-Step Fixes" icon={<Wrench className="h-5 w-5" />}>
          <NumberedList items={issue.fixes} />
        </SectionCard>
      )}

      {(issue.prevention ?? []).length > 0 && (
        <SectionCard title="Prevention" icon={<CheckCircle className="h-5 w-5" />}>
          <BulletList items={issue.prevention} />
        </SectionCard>
      )}

      {notes.length > 0 && (
        <SectionCard title="Your Notes" icon={<Sparkles className="h-5 w-5" />}>
          <div className="space-y-4">
            {notes.map((note: any) => (
              <div
                key={note.id}
                className="rounded-2xl border border-white/10 bg-black/18 p-4 shadow-[0_0_18px_rgba(37,99,235,0.08)]"
              >
                <h3 className="text-lg font-semibold text-white">{note.title}</h3>
                <p className="mt-2 whitespace-pre-wrap text-dtf-text leading-7">
                  {note.content}
                </p>
                {note.createdAt && (
                  <p className="mt-3 text-xs text-dtf-gray">
                    {new Date(note.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}